import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { ArrowLeft, Search, X, Clock, Sparkles, MapPin, Navigation, Store, ShoppingBag, List, Waves, Music, Coffee } from 'lucide-react';
import { motion } from 'framer-motion';
import { Location, LocationCategory } from '../types';
import { BRANDS, BRAND_ICONS, LOCATION_ONLY_BRANDS, SUPERMARKET_BRANDS, ELECTRONICS_BRANDS, type Brand, normalizeBrandName } from '../utils/brands';
import { calculateDistance, formatDistance } from '../utils/distance';
import './AllSearch.css';

interface AllSearchProps {
  options: string[];
  allLocations?: Record<string, Location>;
  userLocation?: [number, number] | null; // App-wide current user location
  onRequestLocation?: () => Promise<[number, number]>; // Trigger app location prompt
  isLocating?: boolean;
  referenceLocation?: [number, number] | null; // Location selected in Home2 (city or current location)
  onBack: () => void;
  onSelect: (locationName: string) => void;
  onCategoryFilter?: (category: LocationCategory) => void;
  onParkAndRideSelect?: (locationName: string) => void;
  onBicycleParkingSelect?: (locationName: string) => void;
  onMobilityParkingSelect?: (locationName: string) => void;
  onCarShareBayOperatorSelect?: (locationName: string) => void;
  allCoffeeLocations?: Record<string, any>;
  onCoffeeSelect?: (cafe: { name: string; latitude: number; longitude: number; [key: string]: any }) => void;
}

interface SearchResult {
  text: string;
  type: 'location' | 'category' | 'park-and-ride' | 'bicycle-parking' | 'mobility-parking' | 'car-share-bay-operator' | 'cafe';
  distance?: number;
  category?: LocationCategory;
  locationData?: Location;
  matchScore?: number;
  cafe?: { name: string; latitude: number; longitude: number; [key: string]: any };
}

// Calculate fuzzy match score (0-1) for search relevance
const calculateMatchScore = (text: string, searchQuery: string): number => {
  const lowerText = text.toLowerCase();
  const lowerSearch = searchQuery.toLowerCase().trim();

  if (lowerText === lowerSearch) return 1.0;
  if (lowerText.startsWith(lowerSearch)) return 0.9;
  if (lowerText.includes(lowerSearch)) return 0.7;

  const searchWords = lowerSearch.split(/\s+/).filter((w) => w.length > 0);
  const textWords = lowerText.split(/\s+/);

  let matchedWords = 0;
  for (const word of searchWords) {
    if (textWords.some((tw) => tw.includes(word) || word.includes(tw))) {
      matchedWords++;
    }
  }

  if (matchedWords === searchWords.length) return 0.6;
  if (matchedWords > 0) return 0.3 + (matchedWords / searchWords.length) * 0.3;

  return 0;
};

// Enhanced search handler (same as SearchDropdown)
const enhancedSearchHandler = (
  search: string,
  options: string[],
  allLocations?: Record<string, Location>,
  userLocation?: [number, number] | null,
  locationMap?: Map<string, Location>
): SearchResult[] => {
  const searchLower = search.toLowerCase().trim();

  if (search.length < 2) {
    return [];
  }

  const results: SearchResult[] = [];
  const locationResults: SearchResult[] = [];
  const shopResults: SearchResult[] = [];
  const seenResults = new Set<string>();

  const brandMatch = BRANDS.find(
    (brand) => brand.toLowerCase() === searchLower || brand.toLowerCase().includes(searchLower)
  );

  const isLocationOnlyBrand = brandMatch && LOCATION_ONLY_BRANDS.includes(brandMatch as Brand);
  
  // Only add category result if search doesn't exactly match the brand
  // This prevents showing the category result when the brand is already in the search bar
  const isExactBrandMatch = brandMatch && brandMatch.toLowerCase() === searchLower;
  
  if (brandMatch && !isExactBrandMatch) {
    results.push({
      text: brandMatch,
      type: 'category',
      category: brandMatch as LocationCategory,
      matchScore: brandMatch.toLowerCase() === searchLower ? 1.0 : 0.8,
    });
  }

  const MAX_RESULTS = 50;
  const searchWords = searchLower.split(/\s+/).filter((w) => w.length > 0);

  for (const option of options) {
    if (locationResults.length + shopResults.length >= MAX_RESULTS) break;

    if (!option || typeof option !== 'string') continue;

    const isShop = option.includes(':::');
    let locationName = '';
    let shopName = '';

    if (isShop) {
      const parts = option.split(' ::: ');
      locationName = parts[0] || '';
      shopName = parts[1] || '';
    } else {
      locationName = option;
    }

    if (isLocationOnlyBrand && isShop) {
      continue;
    }

    const locationLower = locationName.toLowerCase();
    const shopLower = shopName.toLowerCase();

    const locationMatches = locationLower.includes(searchLower);
    const shopMatches = isShop && shopName && shopLower.includes(searchLower);

    let wordMatch = false;
    if (!locationMatches && !shopMatches && searchWords.length > 1) {
      const locationWords = locationLower.split(/\s+/);
      const shopWords = shopLower.split(/\s+/);

      wordMatch = searchWords.every(
        (sw) => locationWords.some((lw) => lw.includes(sw)) || (isShop && shopWords.some((sw2) => sw2.includes(sw)))
      );

      if (!wordMatch) continue;
    }

    const locationScore = locationMatches || wordMatch ? calculateMatchScore(locationName, search) : 0;
    const shopScore = shopMatches || (isShop && wordMatch) ? calculateMatchScore(shopName, search) : 0;

    const finalMatchScore = isShop
      ? Math.max(locationScore, shopScore * 1.3, (locationScore + shopScore) / 2)
      : locationScore;

    const isBrandQuery = BRANDS.some((b) => searchLower.includes(b.toLowerCase()));
    const scoreThreshold = isBrandQuery ? 0.1 : 0.15;

    if (finalMatchScore <= scoreThreshold && !locationMatches && !shopMatches) {
      continue;
    }

    let locationData: Location | undefined;
    let distance: number | undefined;

    if (allLocations && userLocation) {
      if (isShop) {
        locationData = locationMap?.get(locationName.toLowerCase()) || (allLocations[locationName] as Location);
      } else {
        locationData = locationMap?.get(option.toLowerCase()) || (allLocations[option] as Location);
      }

      if (locationData?.latitude && locationData?.longitude) {
        distance = calculateDistance(userLocation[0], userLocation[1], locationData.latitude, locationData.longitude);
      }
    }

    let type: SearchResult['type'] = 'location';
    if (option.startsWith('🚲 ')) {
      type = 'bicycle-parking';
    } else if (option.startsWith('🦽 ')) {
      type = 'mobility-parking';
    } else if (option.startsWith('🚗 ')) {
      type = 'car-share-bay-operator';
    } else if (option.toLowerCase().includes('park and ride') || option.toLowerCase().includes('park & ride')) {
      type = 'park-and-ride';
    }

    const resultKey = `${type}:${option}`;
    if (seenResults.has(resultKey)) continue;
    seenResults.add(resultKey);

    const result: SearchResult = {
      text: option,
      type,
      distance,
      locationData,
      matchScore: finalMatchScore,
      category: locationData?.category,
    };

    if (isShop) {
      shopResults.push(result);
    } else {
      locationResults.push(result);
    }
  }

  const locationCount = locationResults.length;
  const shopCount = shopResults.length;

  locationResults.sort((a, b) => {
    if (a.distance !== undefined && b.distance !== undefined) return a.distance - b.distance;
    if (a.distance !== undefined) return -1;
    if (b.distance !== undefined) return 1;
    return (b.matchScore || 0) - (a.matchScore || 0);
  });

  shopResults.sort((a, b) => {
    if (a.distance !== undefined && b.distance !== undefined) return a.distance - b.distance;
    if (a.distance !== undefined) return -1;
    if (b.distance !== undefined) return 1;
    return (b.matchScore || 0) - (a.matchScore || 0);
  });

  if (isLocationOnlyBrand) {
    results.push(...locationResults);
  } else if (locationCount >= shopCount && locationCount > 0) {
    results.push(...locationResults);
    results.push(...shopResults);
  } else if (shopCount > 0) {
    results.push(...shopResults);
    results.push(...locationResults);
  } else {
    results.push(...locationResults, ...shopResults);
  }

  return results.slice(0, MAX_RESULTS);
};

const AllSearch: React.FC<AllSearchProps> = ({
  options,
  allLocations,
  userLocation,
  onRequestLocation,
  isLocating = false,
  referenceLocation,
  onBack,
  onSelect,
  onCategoryFilter,
  onParkAndRideSelect,
  onBicycleParkingSelect,
  onMobilityParkingSelect,
  onCarShareBayOperatorSelect,
  allCoffeeLocations,
  onCoffeeSelect,
}) => {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState<'recent' | 'coffee' | 'brands' | 'categories' | 'around-me'>('recent');
  const [viewMode, setViewMode] = useState<'recent' | 'coffee' | 'brands' | 'categories' | 'supermarket' | 'electronics' | 'beaches' | 'around-me'>('recent');
  const [recentSearches, setRecentSearches] = useState<string[]>([]);
  const [trendingItems, setTrendingItems] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) clearTimeout(scrollTimeoutRef.current);
      if (coffeeScrollTimeoutRef.current) clearTimeout(coffeeScrollTimeoutRef.current);
    };
  }, []);
  const contentRef = useRef<HTMLDivElement>(null);

  // Create location lookup map
  const locationMap = useMemo(() => {
    if (!allLocations) return new Map<string, Location>();
    const map = new Map<string, Location>();
    for (const [locationId, locationData] of Object.entries(allLocations)) {
      if (locationData && typeof locationData === 'object') {
        const location = locationData as Location;
        const locationName = (location.location || locationId).toLowerCase();
        map.set(locationName, location);
        map.set(locationId.toLowerCase(), location);
      }
    }
    return map;
  }, [allLocations]);

  // Pre-compute brand lowercase for faster matching
  const supermarketBrandsLower = useMemo(() => SUPERMARKET_BRANDS.map((b) => b.toLowerCase()), []);
  const electronicsBrandsLower = useMemo(() => ELECTRONICS_BRANDS.map((b) => normalizeBrandName(b)), []);

  // Load recent searches from localStorage
  useEffect(() => {
    const stored = localStorage.getItem('vibecheck_recent_searches');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setRecentSearches(Array.isArray(parsed) ? parsed : []);
      } catch (e) {
        setRecentSearches([]);
      }
    }
  }, []);

  // Generate trending items from brands and popular locations
  useEffect(() => {
    const trending: string[] = [];
    
    // Add top brands
    BRANDS.slice(0, 10).forEach((brand) => {
      trending.push(brand);
    });

    // Add popular locations (shopping centres, beaches, etc.)
    if (allLocations) {
      Object.entries(allLocations).forEach(([key, loc]) => {
        if (trending.length >= 20) return;
        const locationName = loc.location || key;
        if (locationName.toLowerCase().includes('westfield') || 
            locationName.toLowerCase().includes('beach') ||
            locationName.toLowerCase().includes('costco')) {
          trending.push(locationName);
        }
      });
    }

    setTrendingItems(trending.slice(0, 20));
  }, [allLocations]);

  // Get supermarket results - 10 closest supermarkets overall
  const getSupermarketResults = useMemo(() => {
    const activeUserLocation = userLocation;
    const supermarkets: SearchResult[] = [];

    if (!allLocations) return [];

    // Search through locations
    for (const [locationId, locationData] of Object.entries(allLocations)) {
      if (!locationData || typeof locationData !== 'object') continue;

      const location = locationData as Location;
      const locationName = location.location || locationId;
      const locationNameLower = locationName.toLowerCase();

      const isSupermarket = supermarketBrandsLower.some((brandLower) => locationNameLower.includes(brandLower));

      if (isSupermarket && location.latitude && location.longitude) {
        const distance = activeUserLocation
          ? calculateDistance(activeUserLocation[0], activeUserLocation[1], location.latitude, location.longitude)
          : undefined;

        supermarkets.push({
          text: locationName,
          type: 'location',
          distance,
          locationData: location,
          matchScore: 1.0,
          category: location.category,
        });
      }
    }

    // Search through shops
    for (const option of options) {
      if (!option || typeof option !== 'string') continue;

      const isShop = option.includes(':::');
      if (!isShop) continue;

      const parts = option.split(' ::: ');
      const locationName = parts[0] || '';
      const shopName = parts[1] || '';
      const shopNameLower = shopName.toLowerCase();

      const isSupermarket = supermarketBrandsLower.some((brandLower) => shopNameLower.includes(brandLower));

      if (isSupermarket) {
        const locationData = locationMap.get(locationName.toLowerCase());
        const distance =
          locationData && locationData.latitude && locationData.longitude && activeUserLocation
            ? calculateDistance(
                activeUserLocation[0],
                activeUserLocation[1],
                locationData.latitude,
                locationData.longitude
              )
            : undefined;

        supermarkets.push({
          text: `${locationName} ::: ${shopName}`,
          type: 'location',
          distance,
          locationData: locationData,
          matchScore: 1.0,
        });
      }
    }

    supermarkets.sort((a, b) => {
      if (a.distance !== undefined && b.distance !== undefined) {
        return a.distance - b.distance;
      }
      if (a.distance !== undefined) return -1;
      if (b.distance !== undefined) return 1;
      return a.text.localeCompare(b.text);
    });

    return supermarkets.slice(0, 10);
  }, [allLocations, options, userLocation, locationMap, supermarketBrandsLower]);

  // Get electronics results - 10 closest electronics stores overall
  const getElectronicsResults = useMemo(() => {
    const activeUserLocation = userLocation;
    const electronics: SearchResult[] = [];

    if (!allLocations) return [];

    // Search through locations
    for (const [locationId, locationData] of Object.entries(allLocations)) {
      if (!locationData || typeof locationData !== 'object') continue;

      const location = locationData as Location;
      const locationName = location.location || locationId;
      const locationNameLower = locationName.toLowerCase();

      // Exclude "adams apple" from electronics stores
      if (locationNameLower.includes('adams apple')) {
        continue;
      }

      const isElectronics = electronicsBrandsLower.some((brandNormalized) =>
        locationNameLower.includes(brandNormalized)
      );

      if (isElectronics && location.latitude && location.longitude) {
        const distance = activeUserLocation
          ? calculateDistance(activeUserLocation[0], activeUserLocation[1], location.latitude, location.longitude)
          : undefined;

        electronics.push({
          text: locationName,
          type: 'location',
          distance,
          locationData: location,
          matchScore: 1.0,
          category: location.category,
        });
      }
    }

    // Search through shops
    for (const option of options) {
      if (!option || typeof option !== 'string') continue;

      const isShop = option.includes(':::');
      if (!isShop) continue;

      const parts = option.split(' ::: ');
      const locationName = parts[0] || '';
      const shopName = parts[1] || '';
      const shopNameLower = shopName.toLowerCase();

      // Exclude "adams apple" from electronics stores
      if (shopNameLower.includes('adams apple')) {
        continue;
      }

      const isElectronics = electronicsBrandsLower.some((brandNormalized) => shopNameLower.includes(brandNormalized));

      if (isElectronics) {
        const locationData = locationMap.get(locationName.toLowerCase());
        const distance =
          locationData && locationData.latitude && locationData.longitude && activeUserLocation
            ? calculateDistance(
                activeUserLocation[0],
                activeUserLocation[1],
                locationData.latitude,
                locationData.longitude
              )
            : undefined;

        electronics.push({
          text: `${locationName} ::: ${shopName}`,
          type: 'location',
          distance,
          locationData: locationData,
          matchScore: 1.0,
        });
      }
    }

    electronics.sort((a, b) => {
      if (a.distance !== undefined && b.distance !== undefined) {
        return a.distance - b.distance;
      }
      if (a.distance !== undefined) return -1;
      if (b.distance !== undefined) return 1;
      return a.text.localeCompare(b.text);
    });

    return electronics.slice(0, 10);
  }, [allLocations, options, userLocation, locationMap, electronicsBrandsLower]);

  // Get beaches results - all beaches sorted by distance
  const getBeachesResults = useMemo(() => {
    const activeUserLocation = userLocation;
    const beaches: SearchResult[] = [];

    if (!allLocations) return [];

    for (const [locationId, locationData] of Object.entries(allLocations)) {
      if (!locationData || typeof locationData !== 'object') continue;

      const location = locationData as Location;
      const locationName = location.location || locationId;

      const isBeach = location.category === 'Beaches';

      if (isBeach && location.latitude && location.longitude) {
        const distance = activeUserLocation
          ? calculateDistance(activeUserLocation[0], activeUserLocation[1], location.latitude, location.longitude)
          : undefined;

        beaches.push({
          text: locationName,
          type: 'location',
          distance,
          locationData: location,
          matchScore: 1.0,
          category: location.category,
        });
      }
    }

    beaches.sort((a, b) => {
      if (a.distance !== undefined && b.distance !== undefined) {
        return a.distance - b.distance;
      }
      if (a.distance !== undefined) return -1;
      if (b.distance !== undefined) return 1;
      return a.text.localeCompare(b.text);
    });

    return beaches;
  }, [allLocations, userLocation]);

  // Coffee cafes list (for Coffee tab – browse and search)
  const coffeeCafesList = useMemo(() => {
    if (!allCoffeeLocations || typeof allCoffeeLocations !== 'object') return [];
    const refLocation = userLocation || referenceLocation;
    const list: Array<{ name: string; latitude: number; longitude: number; distance?: number; [key: string]: any }> = [];
    for (const [name, data] of Object.entries(allCoffeeLocations)) {
      if (!data || typeof data !== 'object' || data.latitude == null || data.longitude == null) continue;
      const cafe = { name: data.name || name, latitude: data.latitude, longitude: data.longitude, ...data };
      if (refLocation) {
        cafe.distance = calculateDistance(refLocation[0], refLocation[1], data.latitude, data.longitude);
      }
      list.push(cafe);
    }
    list.sort((a, b) => {
      if (a.distance != null && b.distance != null) return a.distance - b.distance;
      if (a.distance != null) return -1;
      if (b.distance != null) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
    return list;
  }, [allCoffeeLocations, userLocation, referenceLocation]);

  const coffeeSearchResults = useMemo((): SearchResult[] => {
    if (search.length < 2 || !coffeeCafesList.length) return [];
    const searchLower = search.toLowerCase().trim();
    const results: SearchResult[] = [];
    for (const cafe of coffeeCafesList) {
      const name = cafe.name || '';
      if (!name.toLowerCase().includes(searchLower)) continue;
      results.push({
        text: name,
        type: 'cafe',
        distance: cafe.distance,
        cafe,
        matchScore: name.toLowerCase() === searchLower ? 1 : 0.8,
      });
    }
    results.sort((a, b) => {
      if (a.distance != null && b.distance != null) return a.distance - b.distance;
      if (a.distance != null) return -1;
      if (b.distance != null) return 1;
      return 0;
    });
    return results.slice(0, 50);
  }, [search, coffeeCafesList]);

  // Search results
  const searchResults = useMemo(() => {
    const activeUserLocation = userLocation;

    // If current location is set and search is empty, show all nearby locations
    if (userLocation && search.length === 0) {
      const allLocationResults: SearchResult[] = [];
      const MAX_AROUND_ME_RESULTS = 50;

      if (allLocations) {
        for (const [locationId, locationData] of Object.entries(allLocations)) {
          if (allLocationResults.length >= MAX_AROUND_ME_RESULTS) break;

          if (!locationData || typeof locationData !== 'object') continue;

          const location = locationData as Location;
          if (location.latitude && location.longitude) {
            const distance = calculateDistance(
              userLocation[0],
              userLocation[1],
              location.latitude,
              location.longitude
            );

            allLocationResults.push({
              text: location.location || locationId,
              type: 'location',
              distance,
              locationData: location,
              matchScore: 1.0,
              category: location.category,
            });
          }
        }
      }

      return allLocationResults
        .sort((a, b) => {
          if (a.distance !== undefined && b.distance !== undefined) {
            return a.distance - b.distance;
          }
          return 0;
        })
        .slice(0, MAX_AROUND_ME_RESULTS);
    }

    if (search.length < 2) return [];
    // Use current location first, then reference location, then userLocation as fallback
    const searchLocation = userLocation || referenceLocation;
    return enhancedSearchHandler(search, options, allLocations, searchLocation, locationMap);
  }, [search, options, allLocations, userLocation, referenceLocation, locationMap]);

  // Sorted results by distance - filter out category results when search matches a brand
  const sortedResults = useMemo(() => {
    const searchLower = search.toLowerCase().trim();
    const isBrandSearch = BRANDS.some(
      (brand) => searchLower === brand.toLowerCase() || searchLower.includes(brand.toLowerCase())
    );
    
    // Filter out category results if the search is a brand name
    const filteredResults = isBrandSearch
      ? searchResults.filter((result) => result.type !== 'category')
      : searchResults;
    
    // Priority 1: Current user location (if enabled via "Around Me")
    const currentLocation = userLocation;
    // Priority 2: Reference location from Home2 (selected city or current location)
    const refLocation = referenceLocation;
    
    return [...filteredResults].sort((a, b) => {
      // Always prioritize category results at the top
      if (a.type === 'category') return -1;
      if (b.type === 'category') return 1;
      
      // Helper function to calculate distance from a location
      const getDistance = (result: SearchResult, fromLocation: [number, number] | null): number | null => {
        if (!fromLocation || !result.locationData || !result.locationData.latitude || !result.locationData.longitude) {
          return null;
        }
        return calculateDistance(
          fromLocation[0],
          fromLocation[1],
          result.locationData.latitude,
          result.locationData.longitude
        );
      };
      
      // Priority 1: Rank by current location (if enabled)
      if (currentLocation) {
        const aDistCurrent = getDistance(a, currentLocation);
        const bDistCurrent = getDistance(b, currentLocation);
        
        if (aDistCurrent !== null && bDistCurrent !== null) {
          return aDistCurrent - bDistCurrent;
        }
        if (aDistCurrent !== null) return -1;
        if (bDistCurrent !== null) return 1;
      }
      
      // Priority 2: Rank by reference location (from Home2)
      if (refLocation) {
        const aDistRef = getDistance(a, refLocation);
        const bDistRef = getDistance(b, refLocation);
        
        if (aDistRef !== null && bDistRef !== null) {
          return aDistRef - bDistRef;
        }
        if (aDistRef !== null) return -1;
        if (bDistRef !== null) return 1;
      }
      
      // Fallback to match score when no location or distances are equal
      return (b.matchScore || 0) - (a.matchScore || 0);
    });
  }, [searchResults, search, userLocation, referenceLocation]);

  // When user has typed, show all results: locations + cafes (combined, sorted by distance then match)
  const combinedSearchResults = useMemo(() => {
    if (search.length < 2) return sortedResults;
    const seen = new Set<string>();
    const combined: SearchResult[] = [];
    for (const r of sortedResults) {
      const key = r.type === 'cafe' ? `cafe:${r.text}` : r.text;
      if (!seen.has(key)) {
        seen.add(key);
        combined.push(r);
      }
    }
    for (const r of coffeeSearchResults) {
      const key = `cafe:${r.text}`;
      if (!seen.has(key)) {
        seen.add(key);
        combined.push(r);
      }
    }
    combined.sort((a, b) => {
      if (a.type === 'category') return -1;
      if (b.type === 'category') return 1;
      const aDist = a.distance ?? 1e9;
      const bDist = b.distance ?? 1e9;
      if (aDist !== bDist) return aDist - bDist;
      return (b.matchScore || 0) - (a.matchScore || 0);
    });
    return combined.slice(0, 60);
  }, [search, sortedResults, coffeeSearchResults]);

  const handleSelect = (result: SearchResult) => {
    if (!result.text || typeof result.text !== 'string') return;

    // For category/brand results, populate search bar and keep search open
    if (result.type === 'category') {
      // Set the search text to the brand/category name
      setSearch(result.text);
      // Save to recent searches
      const updated = [result.text, ...recentSearches.filter((s) => s !== result.text)].slice(0, 10);
      setRecentSearches(updated);
      localStorage.setItem('vibecheck_recent_searches', JSON.stringify(updated));
      // Don't close the search - let the user see the filtered results
      // The category result will automatically disappear from results since search matches it
      return;
    }

    // Save to recent searches
    const updated = [result.text, ...recentSearches.filter((s) => s !== result.text)].slice(0, 10);
    setRecentSearches(updated);
    localStorage.setItem('vibecheck_recent_searches', JSON.stringify(updated));

    if (result.type === 'location') {
      onSelect(result.text);
      onBack(); // Close AllSearch after selection
    } else if (result.type === 'park-and-ride' && onParkAndRideSelect) {
      onParkAndRideSelect(result.text);
      onBack(); // Close AllSearch after selection
    } else if (result.type === 'bicycle-parking' && onBicycleParkingSelect) {
      onBicycleParkingSelect(result.text);
      onBack(); // Close AllSearch after selection
    } else if (result.type === 'mobility-parking' && onMobilityParkingSelect) {
      onMobilityParkingSelect(result.text);
      onBack(); // Close AllSearch after selection
    } else if (result.type === 'car-share-bay-operator' && onCarShareBayOperatorSelect) {
      onCarShareBayOperatorSelect(result.text);
      onBack(); // Close AllSearch after selection
    } else if (result.type === 'cafe' && result.cafe && onCoffeeSelect) {
      onCoffeeSelect(result.cafe);
      onBack();
    }
  };

  const handleTrendingClick = (item: string) => {
    // Find the item in options and select it
    const matchingOption = options.find((opt) => opt.toLowerCase() === item.toLowerCase());
    if (matchingOption) {
      onSelect(matchingOption);
      onBack(); // Close AllSearch after selection
    }
  };

  const handleRecentClick = (searchTerm: string) => {
    // Check if this is a shop (contains " ::: ") - these should trigger modals
    const isShop = searchTerm.includes(' ::: ');
    
    // Check if this is a brand/category (search-only, don't trigger modal)
    if (!isShop) {
      const isBrand = BRANDS.some((brand) => brand.toLowerCase() === searchTerm.toLowerCase());
      if (isBrand) {
        setSearch(searchTerm);
        return;
      }

      // Check if this is a coffee cafe – open CoffeeMapper2 popup instead of location modal
      const termLower = searchTerm.trim().toLowerCase();
      const matchingCafe = coffeeCafesList.find(
        (cafe) => (cafe.name || '').trim().toLowerCase() === termLower
      );
      if (matchingCafe && onCoffeeSelect) {
        onCoffeeSelect(matchingCafe);
        onBack();
        return;
      }
    }
    
    // For actual locations and shops, trigger the modal
    onSelect(searchTerm);
    onBack(); // Close AllSearch after selection
  };

  const clearRecentSearches = () => {
    setRecentSearches([]);
    localStorage.removeItem('vibecheck_recent_searches');
  };

  // Handle scroll on mobile to dismiss keyboard immediately (same as SearchDropdown)
  const handleScroll = useCallback(() => {
    const isMobile = window.innerWidth <= 768;
    if (isMobile) {
      // Immediately blur input to dismiss keyboard when scrolling starts
      if (inputRef.current && document.activeElement === inputRef.current) {
        inputRef.current.blur();
      }
    }
    // Mark as scrolling to prevent accidental selections
    isScrollingRef.current = true;
    recentIsScrollingRef.current = true;
    
    // Reset scroll flags after a delay
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
    }, 150);
    
    if (recentScrollTimeoutRef.current) {
      clearTimeout(recentScrollTimeoutRef.current);
    }
    recentScrollTimeoutRef.current = setTimeout(() => {
      recentIsScrollingRef.current = false;
    }, 150);
  }, []);

  const handleUseCurrentLocation = useCallback(async () => {
    if (!onRequestLocation) return;
    try {
      await onRequestLocation();
      setViewMode('around-me');
      setActiveTab('around-me');
    } catch {
      // User denied or error - optional: show toast
    }
  }, [onRequestLocation]);

  // Update viewMode when activeTab changes
  useEffect(() => {
    setViewMode(activeTab);
  }, [activeTab]);

  // Track touch positions to distinguish between scrolling and tapping
  const touchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const isScrollingRef = useRef<boolean>(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const coffeeTouchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const coffeeIsScrollingRef = useRef<boolean>(false);
  const coffeeScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleCoffeeTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    coffeeTouchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
    coffeeIsScrollingRef.current = false;
    if (coffeeScrollTimeoutRef.current) {
      clearTimeout(coffeeScrollTimeoutRef.current);
    }
  }, []);

  const handleCoffeeTouchMove = useCallback(() => {
    coffeeIsScrollingRef.current = true;
  }, []);

  const handleCoffeeTouchEnd = useCallback((e: React.TouchEvent, cafe: { name: string; latitude: number; longitude: number; [key: string]: any }) => {
    if (!coffeeTouchStartRef.current || !onCoffeeSelect) return;
    const touch = e.changedTouches[0];
    const deltaX = Math.abs(touch.clientX - coffeeTouchStartRef.current.x);
    const deltaY = Math.abs(touch.clientY - coffeeTouchStartRef.current.y);
    const deltaTime = Date.now() - coffeeTouchStartRef.current.time;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const MAX_TAP_DISTANCE = 10;
    const MAX_TAP_TIME = 300;
    if (distance < MAX_TAP_DISTANCE && deltaTime < MAX_TAP_TIME && !coffeeIsScrollingRef.current) {
      e.preventDefault();
      e.stopPropagation();
      onCoffeeSelect(cafe);
      onBack();
    }
    coffeeScrollTimeoutRef.current = setTimeout(() => {
      coffeeTouchStartRef.current = null;
      coffeeIsScrollingRef.current = false;
    }, 100);
  }, [onCoffeeSelect, onBack]);

  const handleTouchStart = useCallback((e: React.TouchEvent, result: SearchResult) => {
    const touch = e.touches[0];
    touchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
    isScrollingRef.current = false;
    
    // Clear any existing scroll timeout
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
  }, []);

  const handleTouchMove = useCallback(() => {
    // Mark as scrolling if touch moves
    isScrollingRef.current = true;
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent, result: SearchResult) => {
    if (!touchStartRef.current) return;

    const touch = e.changedTouches[0];
    const deltaX = Math.abs(touch.clientX - touchStartRef.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartRef.current.y);
    const deltaTime = Date.now() - touchStartRef.current.time;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    // Thresholds for distinguishing tap from scroll
    const MAX_TAP_DISTANCE = 10; // pixels
    const MAX_TAP_TIME = 300; // milliseconds

    // Only trigger selection if:
    // 1. Movement is below threshold (not scrolling)
    // 2. Time is short (quick tap)
    // 3. Not currently scrolling
    if (
      distance < MAX_TAP_DISTANCE &&
      deltaTime < MAX_TAP_TIME &&
      !isScrollingRef.current
    ) {
      e.preventDefault();
      e.stopPropagation();
      handleSelect(result);
    }

    // Reset after a delay to allow scroll detection
    scrollTimeoutRef.current = setTimeout(() => {
      touchStartRef.current = null;
      isScrollingRef.current = false;
    }, 100);
  }, [handleSelect]);

  // Separate touch handlers for recent items, trending items, and brand items
  const recentTouchStartRef = useRef<{ x: number; y: number; time: number } | null>(null);
  const recentIsScrollingRef = useRef<boolean>(false);
  const recentScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleRecentTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    recentTouchStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
    recentIsScrollingRef.current = false;
    
    if (recentScrollTimeoutRef.current) {
      clearTimeout(recentScrollTimeoutRef.current);
    }
  }, []);

  const handleRecentTouchMove = useCallback(() => {
    recentIsScrollingRef.current = true;
  }, []);

  const handleRecentTouchEnd = useCallback((e: React.TouchEvent, searchTerm: string) => {
    if (!recentTouchStartRef.current) return;

    const touch = e.changedTouches[0];
    const deltaX = Math.abs(touch.clientX - recentTouchStartRef.current.x);
    const deltaY = Math.abs(touch.clientY - recentTouchStartRef.current.y);
    const deltaTime = Date.now() - recentTouchStartRef.current.time;
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);

    const MAX_TAP_DISTANCE = 10;
    const MAX_TAP_TIME = 300;

    if (
      distance < MAX_TAP_DISTANCE &&
      deltaTime < MAX_TAP_TIME &&
      !recentIsScrollingRef.current
    ) {
      e.preventDefault();
      e.stopPropagation();
      handleRecentClick(searchTerm);
    }

    recentScrollTimeoutRef.current = setTimeout(() => {
      recentTouchStartRef.current = null;
      recentIsScrollingRef.current = false;
    }, 100);
  }, [handleRecentClick]);

  // Cleanup recent touch timeout on unmount
  useEffect(() => {
    return () => {
      if (recentScrollTimeoutRef.current) {
        clearTimeout(recentScrollTimeoutRef.current);
      }
    };
  }, []);

  const renderResultItem = (result: SearchResult, index: number) => {
    const { text, type, distance, category } = result;
    const isCafe = type === 'cafe';
    const isShop = !isCafe && text.includes(':::');
    const displayText = isShop ? text.split(':::')[1].trim() : text;
    const locationName = isShop ? text.split(':::')[0].trim() : text;

    return (
      <motion.div
        key={`result-${index}`}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.02 }}
        className="allsearch-result-item"
        onClick={(e) => {
          if (!touchStartRef.current) {
            e.preventDefault();
            e.stopPropagation();
            handleSelect(result);
          }
        }}
        onTouchStart={(e) => {
          e.stopPropagation();
          handleTouchStart(e, result);
        }}
        onTouchMove={(e) => {
          handleTouchMove();
        }}
        onTouchEnd={(e) => {
          e.stopPropagation();
          handleTouchEnd(e, result);
        }}
      >
        <div className="allsearch-result-icon">
          {isCafe ? <Coffee size={20} /> : <MapPin size={20} />}
        </div>
        <div className="allsearch-result-content">
          <h3 className="allsearch-result-name">{displayText}</h3>
          {isShop && <p className="allsearch-result-subtitle">{locationName}</p>}
        </div>
        <div className="allsearch-result-right">
          {distance !== undefined && (
            <span className="allsearch-result-distance">{formatDistance(distance)}</span>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <motion.div
      initial={{ x: '100%' }}
      animate={{ x: 0 }}
      exit={{ x: '100%' }}
      transition={{ type: 'spring', damping: 25, stiffness: 300 }}
      className="allsearch-view"
    >
      {/* Header */}
      <div className="allsearch-header">
        <button className="allsearch-back-button" onClick={onBack}>
          <ArrowLeft size={24} />
        </button>
        <div className="allsearch-search-container">
          <div className="allsearch-search-bar">
            <Search size={20} className="allsearch-search-icon" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search"
              value={search || ''}
              onChange={(e) => setSearch(e.target.value)}
              className="allsearch-search-input"
              autoFocus
              size={1}
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
            />
            {search.length > 0 && (
              <button
                onClick={() => {
                  setSearch('');
                  // Reset viewMode to match activeTab when clearing search
                  setViewMode(activeTab);
                }}
                className="allsearch-clear-button"
                type="button"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs - Only show when search is empty */}
      {search.length === 0 && (
        <div className="allsearch-tabs">
          <button
            className={`allsearch-tab ${activeTab === 'recent' ? 'active' : ''}`}
            onClick={() => setActiveTab('recent')}
          >
            Recent
          </button>
          <button
            className={`allsearch-tab ${activeTab === 'coffee' ? 'active' : ''}`}
            onClick={() => setActiveTab('coffee')}
          >
            Coffee
          </button>
          <button
            className={`allsearch-tab ${activeTab === 'brands' ? 'active' : ''}`}
            onClick={() => setActiveTab('brands')}
          >
            Brands
          </button>
          <button
            className={`allsearch-tab ${activeTab === 'categories' ? 'active' : ''}`}
            onClick={() => setActiveTab('categories')}
          >
            Categories
          </button>
          <button
            className={`allsearch-tab ${activeTab === 'around-me' ? 'active' : ''}`}
            onClick={() => {
              if (userLocation) {
                setActiveTab('around-me');
              } else {
                handleUseCurrentLocation();
              }
            }}
            disabled={isLocating}
          >
            {isLocating ? 'Locating...' : 'Around Me'}
          </button>
        </div>
      )}

      {/* Content */}
      <div 
        className={`allsearch-content ${search.length > 0 ? 'allsearch-content-has-results' : ''}`}
        ref={contentRef}
        onScroll={handleScroll}
        onTouchMove={handleScroll}
      >
        {search.length === 0 ? (
          <>
            {/* Recent Searches View */}
            {viewMode === 'recent' && (
              <div className="allsearch-view-content">
                <div className="allsearch-section-header">
                  <Clock size={18} className="allsearch-section-icon" />
                  <h2 className="allsearch-section-title">Recent Searches</h2>
                  {recentSearches.length > 0 && (
                    <button className="allsearch-clear-recent" onClick={clearRecentSearches}>
                      clear
                    </button>
                  )}
                </div>
                {recentSearches.length > 0 ? (
                  <div className="allsearch-recent-list">
                    {recentSearches.map((recentSearch, index) => (
                      <motion.div
                        key={index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.02 }}
                        className="allsearch-recent-item"
                        onClick={(e) => {
                          // Only handle click if not from a touch event (desktop)
                          if (!recentTouchStartRef.current) {
                          e.preventDefault();
                          e.stopPropagation();
                          handleRecentClick(recentSearch);
                          }
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          handleRecentTouchStart(e);
                        }}
                        onTouchMove={(e) => {
                          handleRecentTouchMove();
                        }}
                        onTouchEnd={(e) => {
                          e.stopPropagation();
                          handleRecentTouchEnd(e, recentSearch);
                        }}
                      >
                        <div className="allsearch-recent-thumbnail">
                          <Clock size={24} />
                        </div>
                        <div className="allsearch-recent-content">
                          <h3 className="allsearch-recent-name">{recentSearch.split(' ::: ')[0] || recentSearch}</h3>
                          {recentSearch.includes(' ::: ') && (
                            <p className="allsearch-recent-subtitle">{recentSearch.split(' ::: ')[1]}</p>
                          )}
                          <p className="allsearch-recent-date">Recently searched</p>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="allsearch-empty">
                    <p>No recent searches</p>
                    <p className="allsearch-empty-hint">Start searching to see your history here</p>
                  </div>
                )}
              </div>
            )}

            {/* Coffee View */}
            {viewMode === 'coffee' && (
              <div className="allsearch-view-content">
                <div className="allsearch-section-header">
                  <Coffee size={18} className="allsearch-section-icon" />
                  <h2 className="allsearch-section-title">Coffee</h2>
                </div>
                {coffeeCafesList.length > 0 ? (
                  <div className="allsearch-results">
                    {coffeeCafesList.map((cafe, index) => (
                      <motion.div
                        key={cafe.name || index}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.02 }}
                        className="allsearch-result-item"
                        onClick={() => {
                          if (onCoffeeSelect) {
                            onCoffeeSelect(cafe);
                            onBack();
                          }
                        }}
                        onTouchStart={(e) => {
                          e.stopPropagation();
                          handleCoffeeTouchStart(e);
                        }}
                        onTouchMove={handleCoffeeTouchMove}
                        onTouchEnd={(e) => {
                          e.stopPropagation();
                          handleCoffeeTouchEnd(e, cafe);
                        }}
                      >
                        <div className="allsearch-result-icon">
                          <Coffee size={20} />
                        </div>
                        <div className="allsearch-result-content">
                          <h3 className="allsearch-result-name">{cafe.name}</h3>
                        </div>
                        <div className="allsearch-result-right">
                          {cafe.distance != null && (
                            <span className="allsearch-result-distance">{formatDistance(cafe.distance)}</span>
                          )}
                        </div>
                      </motion.div>
                    ))}
                  </div>
                ) : (
                  <div className="allsearch-empty">
                    <p>No coffee locations loaded</p>
                    <p className="allsearch-empty-hint">Coffee data will appear here when available</p>
                  </div>
                )}
              </div>
            )}

            {/* Brands View */}
            {viewMode === 'brands' && (
              <div className="allsearch-view-content">
                <div className="allsearch-section-header">
                  <ShoppingBag size={18} className="allsearch-section-icon" />
                  <h2 className="allsearch-section-title">Browse Brands</h2>
                </div>
                <div className="allsearch-brands-grid">
                  {BRANDS.map((brand) => {
                    const IconComponent = BRAND_ICONS[brand];
                    return (
                      <button
                        key={brand}
                        className="allsearch-brand-item"
                        onClick={() => {
                          setSearch(brand);
                          const updated = [brand, ...recentSearches.filter((s) => s !== brand)].slice(0, 10);
                          setRecentSearches(updated);
                          localStorage.setItem('vibecheck_recent_searches', JSON.stringify(updated));
                        }}
                      >
                        <IconComponent size={24} className="allsearch-brand-icon" />
                        <span className="allsearch-brand-name">{brand}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Categories View */}
            {viewMode === 'categories' && (
              <div className="allsearch-view-content">
                <div className="allsearch-section-header">
                  <List size={18} className="allsearch-section-icon" />
                  <h2 className="allsearch-section-title">Categories</h2>
                </div>
                <div className="allsearch-categories-grid">
                  <button
                    className="allsearch-category-item"
                    onClick={() => setViewMode('supermarket')}
                  >
                    <Store size={24} className="allsearch-category-icon" />
                    <span className="allsearch-category-name">Supermarket</span>
                  </button>
                  <button
                    className="allsearch-category-item"
                    onClick={() => setViewMode('electronics')}
                  >
                    <Music size={24} className="allsearch-category-icon" />
                    <span className="allsearch-category-name">Electronics</span>
                  </button>
                  <button
                    className="allsearch-category-item"
                    onClick={() => setViewMode('beaches')}
                  >
                    <Waves size={24} className="allsearch-category-icon" />
                    <span className="allsearch-category-name">Beaches</span>
                  </button>
                </div>
              </div>
            )}

            {/* Supermarket View */}
            {viewMode === 'supermarket' && (
              <div className="allsearch-view-content">
                <div className="allsearch-section-header">
                  <button
                    className="allsearch-back-to-categories"
                    onClick={() => setViewMode('categories')}
                  >
                    <ArrowLeft size={18} />
                  </button>
                  <Store size={18} className="allsearch-section-icon" />
                  <h2 className="allsearch-section-title">Supermarkets</h2>
                </div>
                {getSupermarketResults.length > 0 ? (
                  <div className="allsearch-results">
                    {getSupermarketResults.map((result, index) => renderResultItem(result, index))}
                  </div>
                ) : (
                  <div className="allsearch-empty">
                    <p>No supermarkets found</p>
                  </div>
                )}
              </div>
            )}

            {/* Electronics View */}
            {viewMode === 'electronics' && (
              <div className="allsearch-view-content">
                <div className="allsearch-section-header">
                  <button
                    className="allsearch-back-to-categories"
                    onClick={() => setViewMode('categories')}
                  >
                    <ArrowLeft size={18} />
                  </button>
                  <Music size={18} className="allsearch-section-icon" />
                  <h2 className="allsearch-section-title">Electronics</h2>
                </div>
                {getElectronicsResults.length > 0 ? (
                  <div className="allsearch-results">
                    {getElectronicsResults.map((result, index) => renderResultItem(result, index))}
                  </div>
                ) : (
                  <div className="allsearch-empty">
                    <p>No electronics stores found</p>
                  </div>
                )}
              </div>
            )}

            {/* Beaches View */}
            {viewMode === 'beaches' && (
              <div className="allsearch-view-content">
                <div className="allsearch-section-header">
                  <button
                    className="allsearch-back-to-categories"
                    onClick={() => setViewMode('categories')}
                  >
                    <ArrowLeft size={18} />
                  </button>
                  <Waves size={18} className="allsearch-section-icon" />
                  <h2 className="allsearch-section-title">Beaches</h2>
                </div>
                {getBeachesResults.length > 0 ? (
                  <div className="allsearch-results">
                    {getBeachesResults.map((result, index) => renderResultItem(result, index))}
                  </div>
                ) : (
                  <div className="allsearch-empty">
                    <p>No beaches found</p>
                  </div>
                )}
              </div>
            )}

            {/* Around Me View */}
            {viewMode === 'around-me' && (
              <div className="allsearch-view-content">
                <div className="allsearch-section-header">
                  <Navigation size={18} className="allsearch-section-icon" />
                  <h2 className="allsearch-section-title">Locations Near You</h2>
                </div>
                {isLocating ? (
                  <div className="allsearch-empty">
                    <div className="allsearch-spinner" />
                    <p>Getting your location...</p>
                  </div>
                ) : userLocation && searchResults.length > 0 ? (
                  <div className="allsearch-results">
                    {sortedResults
                      .filter((result) => result.type === 'location' && result.distance !== undefined)
                      .map((result, index) => renderResultItem(result, index))}
                  </div>
                ) : userLocation ? (
                  <div className="allsearch-empty">
                    <p>No locations found nearby</p>
                    <p className="allsearch-empty-hint">Make sure location permissions are enabled</p>
                  </div>
                ) : (
                  <div className="allsearch-empty">
                    <p>Click "Around Me" to find locations near you</p>
                  </div>
                )}
              </div>
            )}
          </>
        ) : (
          /* Search Results – all results (locations + cafes) when typing */
          <div className="allsearch-results">
            {combinedSearchResults.length === 0 ? (
              <div className="allsearch-empty">
                <p>No results found for "{search}"</p>
              </div>
            ) : (
              combinedSearchResults.map((result, index) => renderResultItem(result, index))
            )}
          </div>
        )}
      </div>
    </motion.div>
  );
};

export default AllSearch;

